/**
 * Simulation constants — the single source of truth for the server tick, the world and the
 * growth curve. The API server, the game worker and the browser client all read these, so every
 * value here must be plain data with no runtime dependency beyond the module itself.
 *
 * Every number carries a one-line reason. If you change one, change the reason with it.
 */

/** Server-authoritative simulation runs at a fixed 20 Hz — everything else is derived from it. */
export const TICK_RATE_HZ = 20;

/** 50 ms of simulated time per tick; precomputed so hot loops never divide. */
export const TICK_MS = 1000 / TICK_RATE_HZ;

/** Seconds per tick, for the physics integrator. */
export const TICK_SECONDS = TICK_MS / 1000;

/**
 * The largest room this simulation will run: a hard ceiling, not the size a room opens at.
 *
 * DEFAULT_ROOM_PLAYERS below is what a room actually gets, and it is what every capacity figure
 * on record was measured at. This is the bound `roomSettingsSchema` and `RoomSim` clamp to, so an
 * operator who wants a 200-player room can have one and nobody can ask for a 201-player one.
 *
 * Raising it from 100 to 200 costs close to nothing per player, and that is worth stating
 * plainly because the arithmetic looks like it should: replication is per *viewer* and bounded
 * by interest (INTEREST_MAX_PLAYER_CELLS is 60 whatever the room holds), so 200 players in one
 * room cost roughly what two 100-player rooms cost. It does NOT double a host's capacity — what
 * bounds that is `MEASURED_HOST_LIMITS[kind].maxConcurrentPlayers`, which is a count of sockets
 * and does not care how they are divided into rooms.
 *
 * What it does change is *play*: in a crowd that 60-cell budget is spent at shorter range, so a
 * player in a packed 200-room sees less far away than one in a half-empty 100-room. That is a
 * trade-off an operator is making deliberately, which is why 101..200 is behind the console's
 * out-of-range confirmation (`SAFE_HOST_LIMITS`).
 */
export const ROOM_MAX_PLAYERS = 200;

/**
 * The seats a room opens with, and the size everything in `docs/architecture.md` was measured
 * at: the serving cost per player, the ~500-per-API-process ceiling, the five-worker ladder in
 * `deploy/README.md`. Anything that wants "a normal room" wants this, not the ceiling above.
 */
export const DEFAULT_ROOM_PLAYERS = 100;

/**
 * THE DEFAULT play field, and the size every number in this file was tuned at: square, sized so
 * 100 players at starting mass fill roughly 1% of the area.
 *
 * No longer the *only* size. A room's walls are `RoomSettings.worldWidth` / `.worldHeight`,
 * defaulting to exactly these, and the simulation takes them from its own settings rather than
 * from here — so a 20-player room and a 200-player room need not share a map. These two stay the
 * reference point everything derived below is expressed against, which is what keeps a
 * default-size room bit-for-bit the room it always was.
 */
export const WORLD_WIDTH = 6000;
export const WORLD_HEIGHT = 6000;

/**
 * The smallest world a room may be given, per side.
 *
 * Not a performance bound — a small map is cheap — but a *geometry* one: SPAWN_EDGE_MARGIN,
 * POWER_UP_EDGE_MARGIN and the interest radius are absolute distances, and a world only a few
 * hundred units across would have no interior left to place anything in. 2 000 units is a third
 * of the default and still ten times the widest margin, so every placement range in `RoomSim`
 * stays a range rather than an empty interval.
 */
export const MIN_WORLD_SIZE = 2_000;

/**
 * The largest world a room may be given, per side. `roomSettingsSchema` refuses above it.
 *
 * **What this bound is NOT about is the wire.** A coordinate goes out quantised to a whole world
 * unit (`constants/wire.ts`) and the binary codec writes it as a zigzag LEB128 varint, which
 * grows a byte at a time and is exact to `Number.MAX_SAFE_INTEGER` — there is no fixed field
 * width to overflow and no range the encoder was sized for, at any world size this game could
 * ever be asked for. What a bigger map costs on the wire is one extra varint byte per coordinate
 * past x = 8 191 (zigzag doubles, and 16 383 is the top of a two-byte varint), which is a price,
 * not a correctness cliff. The spatial hash is likewise unbounded here: it aliases only past
 * ~±2 000 000 units, two orders of magnitude above this.
 *
 * What it IS about is the *field*. Pellets and spikes are scaled as densities (see
 * `pelletCountFor` below), so a map four times as wide carries sixteen times the pellets —
 * 52 800 of them at this ceiling, against 3 300 at the default. That is the real cost of a big
 * room, it is paid per tick in the pellet grid and per room in memory, and it is why
 * `SAFE_WORLD_SIZE` stops at twice the default and everything above it needs a confirmation.
 */
export const MAX_WORLD_SIZE = 24_000;

/**
 * How much bigger a room's field is than the default one every constant here was tuned against.
 *
 * Exactly 1 for a default-size room — the numerator and the denominator are the same two
 * literals — which is what makes every derived count below return its tuned value unchanged
 * rather than a rounding of it.
 */
export function worldAreaRatio(worldWidth: number, worldHeight: number): number {
  return (worldWidth * worldHeight) / (WORLD_WIDTH * WORLD_HEIGHT);
}

/** Players spawn at least this far from a world edge so the camera is never half off-field. */
export const SPAWN_EDGE_MARGIN = 200;

/**
 * How long a player who has just joined cannot be touched for, in seconds.
 *
 * **FOUR, WHICH IS THE MIDDLE OF THE REPORT'S OWN RANGE** — "faded or maybe better
 * throbbing/blinking for 3 to 5 seconds in which time they are invisible - meaning they can't be
 * eaten/swallowed". A spawn point is a seeded random draw against SPAWN_EDGE_MARGIN and nothing
 * else: it does not know where the leaders are, so landing inside somebody's mouth is a thing the
 * game does to a player rather than a thing they did. This is the window in which they can drive
 * out of it.
 *
 * It is deliberately **not** DUEL_IMMUNITY_TICKS, which is a second and a half and runs *both*
 * ways. This one is one-way — a protected player is untouchable and still eats — and it is spent
 * the moment they attack with it, so four seconds is long enough to leave a bad spawn and far too
 * short to hunt from. See `PlayerState.spawnProtectedUntilTick`.
 *
 * Three would leave nothing over on a busy edge; five is long enough that somebody who is not
 * moving reads as a bug rather than as a new player.
 */
export const SPAWN_PROTECTION_SECONDS = 4;

/** The same number the simulation counts in. `PlayerState.spawnProtectedUntilTick` is a tick. */
export const SPAWN_PROTECTION_TICKS = SPAWN_PROTECTION_SECONDS * TICK_RATE_HZ;

/** Every player starts at mass 20 — four pellets double that, so the first bite already shows. */
export const STARTING_MASS = 20;

/** Floor mass: below this a player is dead rather than shrinking to a point. */
export const MIN_PLAYER_MASS = 10;

/**
 * Ceiling mass; keeps one runaway leader from covering a meaningful share of the world.
 *
 * Raised from 22 500 on a player's ask for bigger circles. What a third more mass buys is 15% more
 * *width* — a radius is `MASS_RADIUS_COEFFICIENT * sqrt(mass)`, so 692.8 world units against 600 —
 * and everything sized from the ceiling follows it without being told: `PROJECTILE_QUERY_REACH`
 * below, `MAX_CELL_RADIUS` in the simulation, the spectator's view (`SPECTATOR_VIEW_MASS`), and the
 * top rung of the client's zoom ladder, which `constants/view.ts` writes as
 * `massToRadius(MAX_PLAYER_MASS)` rather than as a number for exactly this reason.
 *
 * WHAT IT DOES NOT MOVE IS REPLICATION. `MAX_VIEW_HALF_HEIGHT` is bounded by the width of the
 * arena rather than by how big a player can get, so the widest region anybody is replicated —
 * `INTEREST_RADIUS`, `MAX_VIEW_AREA` and `INTEREST_MAX_ENTITIES` with it — is exactly what it was.
 * The one cost on the wire is a byte: a mass is a varint at `WIRE_MASS_STEP` of 1, so a cell above
 * 16 383 spends three bytes where it spent two, and only cells that far up the curve pay it.
 *
 * The trade a player feels is that the biggest legal blob is a larger share of the screen than it
 * was — about 60% of the narrow axis on a 16:9 window against 52% — because the ladder cannot
 * widen with it. That is stated where the ladder is.
 */
export const MAX_PLAYER_MASS = 30_000;

/**
 * radius = MASS_RADIUS_COEFFICIENT * sqrt(mass), so on-screen *area* is proportional to mass:
 * doubling mass doubles the space a player covers rather than doubling their width.
 */
export const MASS_RADIUS_COEFFICIENT = 4;

/**
 * World units per second at STARTING_MASS; the reference speed the mass curve scales down from.
 *
 * Raised from 300 on the report that raised the ceiling above: "the circle speed should start out
 * even faster ... everything else should stay relative as the size increases and speed decreases
 * (so everything just a little faster)". That is a *scale* rather than a reshaping, so MIN_SPEED
 * went up by the same fifth and SPEED_MASS_EXPONENT did not move at all — every mass in the game
 * runs at exactly 1.2x the speed it did, and every fraction in the curve below is what it was.
 *
 * What is quoted as a multiple of it moves with it: SPECTATOR_PAN_SPEED is `BASE_SPEED * 6`. The
 * projectile speeds deliberately do not — ROCKET_SPEED and GRAPPLE_HAUL_SPEED are absolute numbers
 * tuned against their own ranges and haul windows, so what moved there is the multiple rather than
 * the speed, and both are still well clear of anything a player can steer.
 */
export const BASE_SPEED = 360;

/**
 * speed = BASE_SPEED * (STARTING_MASS / mass) ^ 0.15 — big is slow, but only mildly so.
 *
 * THE CURVE, in world units per second and as a fraction of BASE_SPEED. The raw curve is quoted
 * where the floor is what actually applies:
 *
 *   mass 20 (STARTING_MASS)   360.0   100.0%
 *   mass 200 (10x)            254.9    70.8%
 *   mass 2 000 (100x)         180.4    50.1%
 *   mass 30 000 (MAX)         138.0    38.3%   (raw 120.2, floored)
 *
 * The right-hand column is untouched by BASE_SPEED going 300 -> 360, because MIN_SPEED went up by
 * the same fifth: what follows is about the shape, and the shape did not move.
 *
 * 0.25 was set to spread the slowdown across the whole mass range — it does, but it spreads too
 * much of it: a player who had done nothing more than eat a hundred pellets' worth was down to
 * under a third of the speed they started at, and the top of the range was a fifth. Getting
 * bigger read as being punished rather than as a trade. At 0.15 every 10x of mass costs about
 * 29% of the speed that is left rather than about 44%, so a maxed-out leader keeps 38% of
 * BASE_SPEED where it used to keep 20% — nearly double — and a fresh spawn is 2.6x their speed
 * rather than 5x. That is still a real cost of mass and still enough that a leader can be run
 * down by anyone smaller, which is the risk/reward the genre is built on; it is just no longer
 * the dominant fact about being big.
 *
 * Steeper is what the shape rules out at both ends: at 0.35 the curve hit MIN_SPEED at mass
 * ~2 000, which left the entire top of the range moving at exactly the same speed — no tradeoff
 * at all up there — while already costing a mid-size player 43% of BASE_SPEED.
 */
export const SPEED_MASS_EXPONENT = 0.15;

/**
 * Speed floor so the largest player can still reposition instead of being a sitting target.
 *
 * 138 is 115 raised by the same fifth BASE_SPEED was, so it is the same 38.3% of the reference
 * speed it has been and the raw curve still meets it at mass ~11 950: the floor did not move, the
 * units did. What did move is how much of the range sits on it — with the ceiling at 30 000 this
 * is a safety rail over the last ~2.5x of the mass range rather than the last ~1.9x. That is still
 * the top end rather than the regime most of a match plays in, and the alternative — dropping the
 * floor so the flat top stays the same share of a taller range — would make the biggest players
 * slower in absolute terms than they were, on a card whose whole point is that everybody is
 * faster.
 */
export const MIN_SPEED = 138;

/**
 * What a sprint multiplies a cell's `massToSpeed` by while its player is holding the button.
 *
 * **1.5 RATHER THAN 2, AND THE MASS CURVE IS WHAT DECIDES IT.** The whole spread of
 * SPEED_MASS_EXPONENT is 360 at STARTING_MASS down to MIN_SPEED's 138 at the ceiling — a factor
 * of 2.6, and the risk/reward the genre is built on is that a leader can be run down by anyone
 * smaller. At 1.5 a sprinting maxed-out leader reaches 207, which is still under a fresh spawn's
 * 360 and only just past a mass-2 000 player's 180: a sprint closes or breaks a gap without
 * reversing who is faster. At 2 the same leader reaches 276, which beats every player over about
 * mass 200 outright — a button that inverts the curve for most of the range, so being big would
 * stop being a trade.
 *
 * **AND 2 COMPOSES WITH THE FROST PATCH TO EXACTLY NOTHING.** SLOW_SPEED_MULTIPLIER is 0.5 and
 * the two multiply (see `RoomSim.stepMovement`), so a sprinting player on ice at 2x would be
 * moving at *precisely* their ordinary speed — an ability whose entire effect is a number a
 * sprinter can cancel to 1.000. At 1.5 the pair is 0.75, so ice still costs a quarter of what a
 * sprinter can steer, which is what keeps the patch worth an orb against the players most worth
 * using it on.
 *
 * A third argument the same way: ROCKET_SPEED is 900, tuned so that dodging by *turning* is the
 * skill rather than outpacing one in a straight line. 1.5 puts the fastest thing a player can be
 * at 540; 2 puts it at 720, which is 80% of a rocket and a straight-line escape from one.
 */
export const SPRINT_SPEED_MULTIPLIER = 1.5;

/**
 * A full stamina bar. **The unit is a percentage point, deliberately, so that the bar is its own
 * readout** — the wire carries this number itself as a single varint on the viewer's own frame
 * and a client draws it as a percentage with nothing to convert, exactly as `DuelSnapshot.pot`
 * is 0..100 rather than a fraction of a mass nobody is told.
 */
export const STAMINA_MAX = 100;

/**
 * Stamina spent per second of sprinting: **a full bar buys exactly 4.0 seconds** (80 ticks at
 * STAMINA_DRAIN_PER_TICK's 1.25).
 *
 * The brief is that sprinting is used SPARINGLY, and four seconds is what that means against the
 * numbers already here: at SPRINT_SPEED_MULTIPLIER it is 720 world units of extra travel, a
 * little over a tenth of the default map, and about as long as SPAWN_PROTECTION_SECONDS — long
 * enough to close a lunge or break a chase, far too short to cross the arena on.
 */
export const STAMINA_DRAIN_PER_SECOND = 25;

/**
 * Stamina recovered per second whenever the bar is below full and its owner is not sprinting:
 * **an empty bar takes exactly 16.0 seconds to refill** (320 ticks at STAMINA_REGEN_PER_TICK's
 * 0.3125), which is four times as long as draining it.
 *
 * Four times rather than twice because the sprint has to be *rationed over a run* rather than
 * merely paced: at 2x a player would be sprinting for a fifth of every minute, which is a habit,
 * and at 4x it is about a twentieth, which is a decision. It is also the number that makes the
 * cost legible without a second constant — every second you spend costs you four before you have
 * it back.
 */
export const STAMINA_REGEN_PER_SECOND = 6.25;

/**
 * The least stamina a sprint may **start** on. Four seconds of regen from empty, which is exactly
 * what the four seconds of sprint that emptied it cost.
 *
 * **THERE IS ONE, AND WITHOUT IT "DRAINED" WOULD NOT MEAN ANYTHING.** Drain is four times regen,
 * so a player holding the button on an empty bar would be granted one sprinting tick for every
 * four they waited — a permanent 20%-duty stutter-sprint that never ends and can never be run
 * out of. The minimum is the hysteresis that makes an empty bar an actual pause: it gates
 * *starting* only, so a sprint that is already running continues to zero, and `PlayerState.
 * sprinting` is what tells the two apart.
 *
 * A quarter of the bar rather than the whole of it because being made to wait sixteen seconds for
 * a control you tapped for half a second is a punishment rather than a cost; a second of sprint
 * in hand is the smallest amount that is worth pressing the button for.
 */
export const STAMINA_MIN_SPRINT = 25;

/** Precomputed so `stepMovement` never divides: exactly 1.25, so 80 ticks empty a full bar. */
export const STAMINA_DRAIN_PER_TICK = STAMINA_DRAIN_PER_SECOND * TICK_SECONDS;

/** Precomputed beside it: exactly 0.3125, so 320 ticks refill an empty one. */
export const STAMINA_REGEN_PER_TICK = STAMINA_REGEN_PER_SECOND * TICK_SECONDS;

/**
 * Passive shrink at a *starting dot*, per second — the bottom of the decay curve, not the whole
 * of it. `massDecayPerSecond` (constants/mass.ts) is the curve; see it for the shape and for the
 * table of how long a parked blob takes to come back down.
 *
 * IT USED TO BE THE WHOLE CURVE, at a flat 0.002, and that is the bug this pair replaces. A flat
 * proportional rate means the *time* to fall by a given factor is the same wherever you start, so
 * being enormous cost exactly as little as being small: parked at the ceiling a player still held
 * 20 000 after three and a half minutes and most of an hour later was only just a dot. The report
 * is verbatim "I can park my large circle and not move and I don't die ... we need this to
 * continuously get smaller all the way to a beginning dot", with "we may even want to make it go
 * down faster when you're bigger".
 *
 * 0.005 is 2.5x what the flat rate was and is still nothing to a small player: 0.1 mass a second
 * at STARTING_MASS against roughly 3 a second of pellets a fresh player sweeps up, and 2.6 a
 * second at mass 500 against roughly 9. A pellet is still worth more than a full second of decay
 * up to about mass 480. What it buys is the tail — the last stretch of the descent is governed by
 * this number alone, and at 0.002 that stretch was half an hour on its own.
 */
export const MASS_DECAY_PER_SECOND = 0.005;

/**
 * Passive shrink at MAX_PLAYER_MASS, per second — the top of the same curve, and the number that
 * makes holding the ceiling work rather than a place to sit.
 *
 * Four times the base rate, on a mass 1 500 times larger: 600 mass a second at the ceiling
 * against 0.1 at a dot. A very good player sweeping the pellet field at full size takes in around
 * 40 a second, so the top of the range is not sustainable on pellets by an order of magnitude —
 * it is somewhere you arrive by eating somebody and then have to keep eating to stay. Pellets
 * alone now plateau at roughly mass 2 500 (where 0.65%/s of the mass equals what the field
 * yields) rather than at roughly 14 000, which is the "work harder to maintain the largest sizes"
 * half of the report.
 *
 * Read against the ceiling rather than against STARTING_MASS because the ceiling is what the
 * statement is about, and because everything else tuned to the top of the range is written that
 * way — a card that moves MAX_PLAYER_MASS moves the whole curve with it and the sentences above
 * stay true.
 */
export const MAX_MASS_DECAY_PER_SECOND = 0.02;

/** An attacker must be at least 1.25x the defender's mass to eat them outright. */
export const EAT_MASS_RATIO = 1.25;

/**
 * How much of the *defender's* circle an attacker has to have covered before it may swallow it,
 * as a fraction of the defender's own area.
 *
 * This replaced a centre-containment rule (the defender's centre within 40% of the attacker's
 * radius), which asked the same absolute depth of every defender however big it was. Against a
 * huge attacker that was needlessly generous, and against a wall it was unreachable: both cells
 * are clamped to their own radius at a world edge, so a small blob pinned there can never get
 * its centre closer than the difference of the radii, which for a big enough attacker is further
 * in than any fixed fraction of *its* radius allows. Measured as a share of the defender's area
 * the requirement scales with the pair, so the last of the way in is always reachable — the same
 * reasoning MERGE_OVERLAP_RATIO applies to fusing your own cells.
 *
 * A half is "most of it is in your mouth", and it is also the value that keeps the rule cheap:
 * at a half the centre distance it works out to is provably inside the attacker's own radius
 * (`eatContactDistance`), which is what lets the collision broadphase keep querying that radius.
 * Lowering it below a half would push the reach past the attacker's rim and both the solve's
 * bracket and that query would have to widen with it.
 */
export const EAT_COVERAGE_RATIO = 0.5;

/**
 * The "evenly matched" band: when neither player can eat the other outright, their mass ratio
 * sits inside [DUEL_MASS_RATIO_MIN, DUEL_MASS_RATIO_MAX]. That collision is what starts a
 * mini-game duel — which mini-game is a registry lookup (`@chompdome/shared/minigames`), never a
 * branch in the collision path.
 */
export const DUEL_MASS_RATIO_MIN = 1 / EAT_MASS_RATIO;
export const DUEL_MASS_RATIO_MAX = EAT_MASS_RATIO;

/** Below this mass a matched collision is resolved instantly rather than interrupting play. */
export const DUEL_MIN_MASS = 40;

/**
 * How long a duellist has to lock a choice in — 10 s at TICK_RATE_HZ.
 *
 * This was 2 s, budgeted so that decide plus reveal stayed shorter than the ~3 s a third player
 * needs to cross an INTEREST_RADIUS view. In play that read as no window at all: the duel was
 * over before you had registered it had started. Ten seconds is a deliberate trade of that
 * budget for a window a human can actually think inside — a duel is now long enough that
 * onlookers can gather, which makes the freeze (a duellist cannot be eaten or pulled into
 * another duel) the thing that keeps it fair rather than the clock. Latency was never the
 * binding constraint: the client re-sends an unlocked choice on every input frame until the
 * server acknowledges it.
 */
export const DUEL_DECIDE_TICKS = 200;

/**
 * How long both choices stay on screen after they are revealed, before the mass moves — 0.8 s.
 * Short enough that it is not a cutscene, long enough that losing is legible rather than
 * something that already happened by the time you looked.
 */
export const DUEL_REVEAL_TICKS = 16;

/**
 * Mutual grace after a duel ends: 1.5 s in which neither party can be eaten or pulled into
 * another duel.
 *
 * This is load-bearing, not a courtesy. The winner takes DUEL_MASS_TRANSFER of the loser, which
 * puts the pair *outside* the evenly-matched band and inside EAT_MASS_RATIO — so without a grace
 * window the very next tick would swallow the loser whole and "the loser survives at reduced
 * mass" would be a fiction. What the window buys is a chance to run; a loser who stands still
 * until it expires is eaten, which is the correct lesson.
 *
 * IT IS NOT THE ONLY WINDOW A DUEL ENDS WITH, AND IT IS THE SMALLER ONE. Releasing a duel also
 * grants both sides a full SPAWN_PROTECTION_TICKS shield, because two players who have stood
 * still in the open for as long as the duel took are in exactly the position a bad spawn is in
 * — see `RoomSim.releaseSide`. This one stays what it always was: the *mutual* rule that keeps
 * the pair off each other, running both ways, where the shield is one-way and lets its holder
 * still eat.
 */
export const DUEL_IMMUNITY_TICKS = 30;

/**
 * The stake: the winner takes a quarter of the loser's mass, and the loser lives.
 *
 * Losing a duel must not be death — duels are forced on evenly matched players, and a mechanic
 * that kills you for guessing wrong would make every fair fight something to avoid. A quarter is
 * enough that the winner is decisively bigger afterwards (1.25:1 becomes about 1.67:1, past
 * EAT_MASS_RATIO) without ending the loser's run.
 */
export const DUEL_MASS_TRANSFER = 0.25;

/**
 * Extra clearance, in world units, put between the two sides when a duel pays out.
 *
 * The pair was already pushed just past contact when the duel started — every matched collision
 * is, duel or not. What invalidates that push is the payout: the winner is scaled up by a
 * quarter of the loser, so their radius (and with it their `eatContactDistance` reach) grows
 * while both bodies sit frozen where the original push left them. The winner's mouth therefore
 * closes back over the loser at the moment of release, at every mass — small at duel sizes the
 * game actually sees, but never zero. Re-clearing against the *post*-payout radii is what makes
 * DUEL_IMMUNITY_TICKS a window to run rather than a window to be swallowed in.
 *
 * The margin on top is deliberately small: the loser should start outside the mouth, not be
 * teleported to safety. Their escape is that they are now the lighter and faster of the two.
 */
export const DUEL_RELEASE_CLEARANCE = 4;

/**
 * The ceiling on what ANY duel can take off a loser: **half of their cluster**.
 *
 * A contact duel can never reach it — DUEL_MASS_TRANSFER is a quarter — so this is stated as a
 * rule about duels rather than as a special case for the gun, and the gun is only the reason it
 * had to be written down. A gun duel stakes an absolute number measured in the *smaller* of the
 * two players (DUEL_GUN_STAKE_MULTIPLIER), which is a number the smaller player cannot pay out of
 * their own body: three times your own mass is not a thing you have.
 *
 * **HALF RATHER THAN "EVERYTHING DOWN TO STARTING_MASS", AND THE DIFFERENCE IS AN EXPLOIT.** A
 * floor at the mass a fresh player joins with would let somebody sitting at exactly that mass fire
 * a duel gun with literally nothing on the table — a free roll at three times their own mass,
 * repeatable. A share always costs something and always leaves something, at every size, which is
 * the only shape of clamp that is a rule rather than a threshold to park under.
 *
 * Losing a duel must not be death (see DUEL_MASS_TRANSFER); half a cluster is a bad afternoon.
 */
export const DUEL_MAX_LOSS_SHARE = 0.5;

/**
 * THE DUEL GUN'S STAKE: three times the SMALLER of the two players, fixed at the tick the duel
 * began.
 *
 * A contact duel stakes DUEL_MASS_TRANSFER of whatever the loser turns out to be worth, which
 * only reads as fair because the two are inside the evenly-matched band to begin with. A gun duel
 * is the opposite case by construction — it is fired *across* a mismatch — so a share of the
 * loser would mean the same button was worth a quarter of a leader against a beginner and a
 * quarter of a beginner against a leader. The smaller player is the one whose whole run is on the
 * table, so they are the one the pot is measured in, and both sides put up the same number.
 *
 * **THREE, AND IT IS AN ABSOLUTE MASS RATHER THAN A SHARE.** Against somebody your own size it is
 * twelve times a contact duel's stake, which is what makes pressing the button a decision rather
 * than a free re-roll of a fight you could have had by driving into them. Against somebody much
 * bigger it is the whole reason the ability exists: a beginner who lands one is playing for four
 * times what they are worth, and the leader is playing for a slice of theirs.
 *
 * **IT IS FIXED WHEN THE DUEL STARTS, NOT WHEN IT RESOLVES**, and that is the load-bearing half:
 * DUEL_DECIDE_TICKS is ten seconds in which both players are frozen and neither can eat, so a
 * stake recomputed at resolution would be a number the overlay showed and then quietly changed.
 * See `DuelState.stake`.
 *
 * **NOBODY PAYS THEMSELVES TO DEATH FOR IT**, and nobody plays for free either: every payout is
 * clamped by DUEL_MAX_LOSS_SHARE. What falls out of that clamp is the balance of the whole
 * ability — gunning somebody far *smaller* than you risks three times their mass to win at most
 * half of theirs, so pointing it downward is a bad trade. That is the grapple's rule — the
 * reversal is the same rule as the win — arrived at from the other end.
 */
export const DUEL_GUN_STAKE_MULTIPLIER = 3;

/**
 * How long the player a duel gun pulled in is shielded for once it lets them go — ten seconds,
 * which is DUEL_DECIDE_TICKS, and it is that number rather than a number.
 *
 * A gun duel costs its target ten frozen seconds they did not ask for. If the window afterwards
 * were shorter than the duel itself, two players with a gun each — or one player with the pack's
 * second charge — could hold somebody in duels for longer than they are ever free, which is a
 * room that player is not playing in. Setting it *to* the duel's own length buys the property
 * outright: the seconds you can be held are at most the seconds you are then untouchable for.
 *
 * **IT IS THE SPAWN SHIELD, NOT `duelImmuneUntil`, AND THE DIFFERENCE IS THE WHOLE POINT.** The
 * grace runs BOTH ways — an immune player eats nobody either — so ten seconds of it would punish
 * the person who was gunned for having been gunned. `spawnProtectedUntilTick` is one-way: nothing
 * in the room can reach them, they can still eat, and it ends the moment they attack with it. So
 * a target who wants to spend the window running gets all ten seconds of it, and a target who
 * wants to spend it hunting has spent it. See `RoomSim.releaseSide`.
 *
 * Granted to the TARGET only. The firer chose to be here and gets the ordinary
 * SPAWN_PROTECTION_TICKS both sides of every duel already get.
 */
export const DUEL_GUN_IMMUNITY_TICKS = DUEL_DECIDE_TICKS;

/**
 * CHICKEN'S CLOCK: how many ticks one percentage point of the pot takes.
 *
 * **THE PERCENTAGE IS THE CLOCK, AND THIS IS DERIVED RATHER THAN CHOSEN** — 100 percentage points
 * is DUEL_DECIDE_TICKS exactly, so the pot reaches 100% on the tick a duel's answering window
 * would have closed and the two can never disagree. There is deliberately no CHICKEN_*_TICKS
 * duration constant beside it: a second number for the same ten seconds is a second number to
 * forget to change.
 *
 * It is not a knob. Moving DUEL_DECIDE_TICKS moves this with it, which is the whole point.
 */
export const CHICKEN_TICKS_PER_PERCENT = DUEL_DECIDE_TICKS / 100;

/**
 * The closed range chicken's hidden bust point is drawn uniformly from, in percent.
 *
 * **THE FLOOR IS WHAT MAKES THE GAME PLAYABLE AND THE CEILING IS WHAT MAKES IT END.** Below 50%
 * nothing can burst, so the first half of every round is a staring contest with no risk in it —
 * which is exactly the half where the bonus rungs below are still out of reach, so a player who
 * grabs early is choosing to be paid almost nothing rather than gambling. Above 50% the survival
 * odds fall linearly to zero at 100%, and because the draw is strictly under the ceiling the pot
 * always bursts *before* the answering window closes: a chicken duel is ended by its own clock,
 * never by the duel's timeout, and no side is ever answered for.
 *
 * A hidden point drawn per duel is also the only thing that stops the game being solvable. A
 * fixed one would make "grab at the tick before it" the whole of the strategy, and "whoever grabs
 * first always wins" is the failure this range exists to prevent.
 */
export const CHICKEN_BUST_MIN_PERCENT = 50;
export const CHICKEN_BUST_MAX_PERCENT = 100;

/**
 * The range chicken's hidden ARM point is drawn uniformly from, in percent — the penalty band.
 *
 * A grab strictly before the point a duel drew is **early**: the grabber loses and pays
 * CHICKEN_EARLY_GRAB_PENALTY to the other player instead of taking anything. It is the bust
 * range's mirror image, and it exists because without it the first half of a round was risk-free
 * — nothing can burst below CHICKEN_BUST_MIN_PERCENT, so "grab immediately" cost a player only
 * the pot they did not wait for. Now it costs them.
 *
 * **THE CEILING *IS* CHICKEN_BUST_MIN_PERCENT RATHER THAN A NUMBER EQUAL TO IT**, which is the
 * whole of "there is no safe corridor": the two bands meet at exactly one point, so at every
 * percentage on the bar a player is trading penalty risk against bust risk and there is no
 * stretch anywhere that is free. Deriving the ceiling is what stops a later edit to one band
 * opening a gap in the middle that nobody wrote down.
 *
 * The floor is 0 because that tail is real: one duel in fifty arms immediately and no grab in it
 * is early. Clamping it up would make the penalty certain in a range that says it is not.
 */
export const CHICKEN_ARM_MIN_PERCENT = 0;
export const CHICKEN_ARM_MAX_PERCENT = CHICKEN_BUST_MIN_PERCENT;

/**
 * What an early grab costs its grabber, as a multiple of the duel's ordinary stake, paid to the
 * other player.
 *
 * **FLAT, RATHER THAN SCALED BY HOW EARLY THE GRAB WAS**, and that is what produces the curve the
 * game is about: the odds of being early already fall linearly with the climb, so a flat charge
 * against falling odds is a rising expected value, which crosses zero at about 29% and lets the
 * bonus rungs above it do the rest. A penalty that also grew as the grab got earlier would double
 * the same slope and flatten the rungs out of the column. The whole table is on
 * `chickenPotShare` and is pinned rung by rung in `minigames.test.ts`.
 *
 * 0.4 is where the crossing lands under the low rung rather than on it: below ~29% grabbing is
 * strictly a losing move, between there and 40% it is a thin win, and the first rung is the first
 * place it is properly worth doing. Bigger and the first rung stops being reachable at a profit;
 * smaller and the first quarter of every round is nearly free again, which is the thing this
 * constant exists to end.
 *
 * It is paid through `RoomSim.transferStake` like any other duel payout, so DUEL_MAX_LOSS_SHARE
 * clamps it and an early grab can never kill anybody.
 */
export const CHICKEN_EARLY_GRAB_PENALTY = 0.4;

/**
 * The three rungs the pot's bonus steps up on, in percent.
 *
 * A flat pot — one that is simply worth its own percentage — makes grabbing at 1% strictly
 * correct against a rational opponent, because every tick after it is risk bought with nothing.
 * The rungs are what make waiting worth it: each one is a jump in reward that the risk does not
 * match, so the decision a player actually makes is "do I hold for the next rung", which is the
 * game. See `chickenPotShare` in `@chompdome/shared/minigames/chicken.ts` for the curve they
 * assemble into and the expected-value arithmetic behind these three numbers.
 */
export const CHICKEN_BONUS_RUNG_LOW = 40;
export const CHICKEN_BONUS_RUNG_MID = 60;
export const CHICKEN_BONUS_RUNG_HIGH = 80;

/**
 * What the pot is multiplied by once the climb is past each rung. Below the low rung it is 1 —
 * the pot is worth its own percentage and nothing more.
 *
 * Each is 1.5x the one under it, which is the shape rather than three independently tuned
 * numbers: a constant ratio between rungs means the reward for holding one rung longer is the
 * same *decision* wherever you are on the curve, while the risk of holding it is not — the odds
 * of surviving another rung get worse every time. That asymmetry is what makes 80% both the best
 * place to grab and a bad place to stand.
 */
export const CHICKEN_BONUS_LOW = 1.5;
export const CHICKEN_BONUS_MID = 2.25;
export const CHICKEN_BONUS_HIGH = 3.5;

/**
 * Steady-state pellet population per room.
 *
 * 3 300 is 3× the previous 1 100. The mass band below is scaled down to 2–4 so net growth rate
 * is preserved:
 *
 *   OLD: 1 100 pellets × 8 mean mass = 8 800 mass-units in the field
 *   NEW: 3 300 pellets × 2.5 mean mass = 8 250 mass-units in the field
 *
 * A player sweeps `2 × radius × speed × density × pelletMass` per second. With 3× density and
 * ~0.31× mass, the product is unchanged — growth rate is the same, but the field feels alive
 * with movement because there are three times as many targets.
 */
export const PELLET_COUNT = 3300;

/**
 * THE PELLET SIZE BAND. A pellet is drawn from [PELLET_MASS_MIN, PELLET_SPAWN_MASS_MAX] at
 * spawn and then grows toward PELLET_MASS_MAX for as long as nobody eats it.
 *
 * The band is scaled down to preserve growth rate at 3× pellet count:
 *
 *   OLD: 1 100 × (6+10)/2 = 8 800 total mass, mean spawn 8, range 6–14
 *   NEW: 3 300 × (2+3)/2  = 8 250 total mass, mean spawn 2.5, range 2–4
 *
 * With PELLET_RADIUS_COEFFICIENT raised from 2.1 to 3.5, the drawn sizes are:
 *
 *   OLD 8-mass: 2.1 × √8  = 5.9 units
 *   NEW 2.5-mass: 3.5 × √2.5 = 5.5 units (fresh)
 *   NEW 4-mass: 3.5 × √4 = 7.0 units (grown)
 *
 * Visually similar per-dot but 3× the dots on screen, which is the user's request: "way more
 * dots, bigger looking, but NOT grow the circle as quickly."
 *
 * Growth: at 0.001 per tick a 2-mass pellet reaches the 4-mass ceiling in 100 s; a 3-mass one
 * in 50 s. Quiet corners still fill with fatter dots.
 */
export const PELLET_MASS_MIN = 2;
export const PELLET_SPAWN_MASS_MAX = 3;
export const PELLET_MASS_MAX = 4;
export const PELLET_GROWTH_PER_TICK = 0.001;

/**
 * The reference pellet: the middle of the spawn band, and the size every piece of tuning
 * arithmetic in this file is quoted against.
 *
 * No pellet is exactly this any more — the band above is what a real one is drawn from — so this
 * is a stand-in for "a pellet", not a value the simulation reads. It is still a quarter of
 * STARTING_MASS-ish, which is what keeps a single dot a size step you can see and a handful of
 * them a doubling.
 */
export const PELLET_MASS = (PELLET_MASS_MIN + PELLET_SPAWN_MASS_MAX) / 2;

/**
 * radius = PELLET_RADIUS_COEFFICIENT * sqrt(mass), so a pellet's *area* is proportional to its
 * mass exactly as a player's is — a dot worth twice as much covers twice the ground.
 *
 * Raised from 2.1 to 3.5 so that the lighter pellets (2–4 mass) still read as visible dots:
 *
 *   OLD 8-mass @ 2.1: 5.9 units
 *   NEW 2.5-mass @ 3.5: 5.5 units (fresh), 7.0 units (grown to 4-mass)
 *
 * The coefficient is 1.67× larger but the mass is 0.31× smaller, so the visible radius is
 * similar — but the user asked for "bigger looking" so the grown pellets end up slightly larger.
 */
export const PELLET_RADIUS_COEFFICIENT = 3.5;

/** The largest a pellet can ever draw. Spawn keeps this much clear of every world edge. */
export const PELLET_MAX_RADIUS = PELLET_RADIUS_COEFFICIENT * Math.sqrt(PELLET_MASS_MAX);

/**
 * THE EJECTED PELLET — the oversized dot a player shoots out as bait (see EJECT_MASS_COST).
 *
 * Three times the largest an ordinary pellet ever grows to, which is 4.8x the mean one a room
 * actually spawns. On screen that is what the user asked for: `pelletRadius` puts it at 12.1
 * units against 5.5 for a fresh dot and 7.0 for a fully grown one — 2.2x and 1.7x the radius —
 * so a bait dot is unmistakable in a field of ordinary food without being mistakable for a
 * player. It sits above PELLET_MASS_MAX on purpose, which is also what makes it *inert* on the
 * growth curve: `pelletMassAt` never takes a pellet below its own spawn mass and never grows one
 * past the larger of PELLET_MASS_MAX and that mass, so an ejected pellet is worth exactly this
 * from the tick it is thrown until somebody eats it.
 *
 * It is worth less than it cost (EJECT_MASS_COST), and that difference is the whole price of
 * baiting: the room gets the rest.
 */
export const EJECTED_PELLET_MASS = 3 * PELLET_MASS_MAX;

/** How big an ejected pellet draws, and the clearance its flight is clamped to the world by. */
export const EJECTED_PELLET_RADIUS = PELLET_RADIUS_COEFFICIENT * Math.sqrt(EJECTED_PELLET_MASS);

/**
 * The top of the band the wire quantises a pellet's mass across — the *ejected* mass, not
 * PELLET_MASS_MAX.
 *
 * This is how an oversized pellet is told apart from an ordinary one without a new wire field.
 * `size` spans [PELLET_MASS_MIN, this], so a fully grown ordinary pellet lands at step 51 and
 * everything above that is only reachable by something that was ejected. A per-pellet flag would
 * have been a byte on the tier that *is* the replication bottleneck, to say something the size
 * already says.
 */
export const PELLET_WIRE_MASS_MAX = EJECTED_PELLET_MASS;

/**
 * How finely a pellet's mass is quantised for the wire: `size` is an integer in [0, 255] across
 * the band, one byte, worth 0.039 mass or 0.010 units of radius a step.
 *
 * The band runs to PELLET_WIRE_MASS_MAX rather than to PELLET_MASS_MAX, because an ejected
 * pellet rides this same field — see PELLET_WIRE_MASS_MAX. That widened a step by a quarter and
 * is still two orders of magnitude below anything a viewer can see.
 *
 * The pellet tier is the bulk of replication and replication is the bottleneck by 40-70x, so a
 * float per pellet was never an option — this is the one field the size mechanic adds to a
 * pellet frame, it is sent once when a pellet enters a viewer's interest set, and the client
 * carries it forward on the growth curve rather than being re-told.
 */
export const PELLET_SIZE_STEPS = 255;

/**
 * DENSITY HOTSPOTS — why the field is patchy instead of sprinkled.
 *
 * A uniform draw over 6000x6000 gives every part of the map the same amount of food, which is
 * the most boring possible map: nowhere is worth going and nowhere is worth avoiding. Instead a
 * room draws PELLET_HOTSPOT_COUNT centres from its own seeded stream, once, at construction, and
 * PELLET_HOTSPOT_SHARE of every pellet it ever spawns lands in one of them. The rest are spread
 * uniformly, which is what stops the space between hotspots from being empty.
 *
 * Because the centres belong to the room and not to the opening field, a respawn refills the
 * same shape the room started with — the patchiness is a property of the map, not a transient
 * that flattens back to uniform over the first few minutes.
 *
 * The offset inside a hotspot is drawn as `radius * u` rather than `radius * sqrt(u)`, so areal
 * density falls off as 1/r from a dense core instead of filling a disc evenly to a hard rim: a
 * hotspot has to blend into the background or it reads as a circle somebody drew.
 *
 * 10 centres of radius 600 cover about a third of the world, so three quarters of the food sits
 * in a third of the space — call it 2.4x the average density inside a hotspot and 0.36x outside,
 * before the 1/r core concentrates it further. Centres are placed at least their own radius from
 * every edge, so a hotspot disc is always wholly inside the world and nothing piles up in a
 * corner against the clamp.
 *
 * MIN_SEPARATION is a *replication* constraint, not an aesthetic one, and it is the one number
 * on this page that interest management cares about. `selectInterest` ranks every pellet inside
 * a viewer's interest radius and keeps what fits, so what a view costs to build is how many
 * candidates it handles — and stacked hotspot cores are the only thing in this distribution that
 * can put a lot of them in one place. Unseparated, three or four cores landing together took
 * the worst spot on the map to ~380 candidates against the old uniform field's flat ~106. At
 * half again a hotspot's own radius the cores stay apart while the discs still overlap enough
 * not to read as circles somebody drew: the busiest spot on the map falls to ~200 and the p99 to
 * ~180, while the *median* spot drops to ~80 — cheaper than the uniform field was, because there
 * are fewer pellets overall. Widening it further buys nothing, because at two radii ten discs no
 * longer comfortably fit and the retry starts giving up. Placement retries against the constraint
 * a bounded number of times and then takes what it drew, exactly as SPIKE_MIN_SEPARATION does,
 * so room creation never depends on how lucky the seed was.
 */
export const PELLET_HOTSPOT_COUNT = 10;
export const PELLET_HOTSPOT_RADIUS = 600;
export const PELLET_HOTSPOT_SHARE = 0.75;
export const PELLET_HOTSPOT_MIN_SEPARATION = 1.5 * PELLET_HOTSPOT_RADIUS;
export const PELLET_HOTSPOT_PLACEMENT_ATTEMPTS = 32;

/**
 * Pellets respawn at 75/s — 3× the previous 25/s to match 3× the pellet count.
 *
 * A fully cleared room refills in 3 300 / 75 = 44 s, similar to the old 1 100 / 25 = 44 s apply.
 * PELLET_RESPAWN_NEAR_PLAYER_SHARE of these spawn near live players so the user sees dots
 * appearing around them rather than only in distant corners.
 */
export const PELLET_RESPAWN_PER_SECOND = 75;

/**
 * Fraction of respawning pellets that spawn near a random live player rather than at hotspots.
 * The player will see dots appearing in their view, which is the user's request: "they should
 * see the dots appearing as they're being added."
 */
export const PELLET_RESPAWN_NEAR_PLAYER_SHARE = 0.4;

/**
 * Minimum clearance from a player's centre when spawning a pellet near them, in world units.
 * Prevents pellets from spawning directly on top of a player (which would be instantly eaten).
 */
export const PELLET_NEAR_PLAYER_MIN_DISTANCE = 80;

/**
 * Maximum distance from a player when spawning a pellet near them. Just inside the typical
 * interest radius so the spawn is visible to that player.
 */
export const PELLET_NEAR_PLAYER_MAX_DISTANCE = 600;

/**
 * INTEREST MANAGEMENT LIVES IN `view.ts`, not here.
 *
 * How far a client is replicated is no longer a constant: it is the half-diagonal of what that
 * client's camera frames, which depends on the player's mass and the shape of their screen. Both
 * halves of that — the camera's band ladder and the interest region that has to cover it — are
 * in `./view.js`, together, because keeping them in two files is exactly how they came to
 * disagree. `INTEREST_RADIUS`, `INTEREST_TARGET_ENTITIES` and `INTEREST_MAX_ENTITIES` are still
 * exported from `@chompdome/shared/constants`; they are derived there rather than written down.
 */

/** Leaderboard shows the top 10 by mass. */
export const LEADERBOARD_SIZE = 10;

/**
 * Tiered replication rates, in Hz. WebSocket replication — not simulation — is the scaling
 * bottleneck, so each entity class gets the slowest rate it can tolerate.
 *
 * `pellet` is 0 on purpose: pellets are static once spawned and replicate event-driven (on the
 * delta that creates or consumes them), never on a timer.
 *
 * `roster` is the slowest tier there is, and it is slow because it is the widest: the frame it
 * drives names *every* player in the room rather than the ones in a viewer's interest set (see
 * `serverRosterSchema`). At ROOM_MAX_PLAYERS that is a few kilobytes, so it is built once per
 * room, encoded once, and sent at half the leaderboard's rate — a second's lag on "who is in
 * here" is not something anybody can perceive, and it is the difference between the panel
 * costing a rounding error and costing a tenth of the frame budget it sits beside.
 */
export const UPDATE_RATE_HZ = {
  player: 20,
  projectile: 20,
  powerup: 5,
  pellet: 0,
  leaderboard: 2,
  roster: 1,
} as const;

export type UpdateChannel = keyof typeof UPDATE_RATE_HZ;

/**
 * The same table expressed as a tick interval, which is what the worker loop actually uses.
 * 0 means "never on a timer" (see `pellet` above). Kept in sync with UPDATE_RATE_HZ by test.
 */
export const UPDATE_INTERVAL_TICKS: Record<UpdateChannel, number> = {
  player: 1,
  projectile: 1,
  powerup: 4,
  pellet: 0,
  leaderboard: 10,
  roster: 20,
};

/** Drop a connection that has sent no input or ping for 10 s. */
export const CLIENT_TIMEOUT_MS = 10_000;

/** Clients ping every 2 s; three missed pings trip CLIENT_TIMEOUT_MS. */
export const CLIENT_PING_INTERVAL_MS = 2_000;

/** Inputs are accepted at most one per tick per connection; the rest are dropped, not queued. */
export const MAX_INPUTS_PER_TICK = 1;

/**
 * A player is a *cluster* of cells, and this is the most it may hold.
 *
 * 16 is set by replication, not by simulation. INTEREST_MAX_ENTITIES is the per-snapshot budget
 * and a viewer's own cluster is always sent whole, so a larger cap would let one player's own
 * body crowd its neighbours out of its own view — the one thing interest management must never
 * do. It also bounds the same-player separation pass at 120 pairs per player per tick, which a
 * full room absorbs well inside the tick budget.
 */
export const MAX_CELLS_PER_PLAYER = 16;

/**
 * Ticks a newly created cell must wait before it may merge back into a sibling — 60 ticks is 3 s
 * at TICK_RATE_HZ. It is a tick count and not a timestamp on purpose: a tick count replays
 * identically, a wall clock does not.
 *
 * It is a *grace window*, and nothing more. Fragments are laid out inside the body they came out
 * of, so on the tick of a shatter they overlap almost completely; without this they would satisfy
 * MERGE_OVERLAP_RATIO on the spot and the shatter would undo itself. The cooldown is what gives
 * SAME_PLAYER_SEPARATION time to push them clear.
 *
 * The cooldown expiring must never *cause* a merge, and since the gather spring was removed it
 * cannot: an expired pair sitting apart is under no force at all, and stays where it is until
 * the player drives one cell into the other.
 */
export const MERGE_COOLDOWN_TICKS = 60;

/**
 * Fraction of a same-player overlap resolved per tick while the merge cooldown runs. Clearing
 * all of it in one tick makes a freshly split cluster explode outward; a quarter breaks contact
 * in about a fifth of a second while the cluster still reads as one body.
 *
 * **It is the OUTWARD half of the same-player force, and CLUSTER_MAGNET_SECONDS below is the
 * inward one.** It used to be the only one at all: a pair of gather constants
 * (SAME_PLAYER_REJOIN, SAME_PLAYER_SETTLE) were deleted at a player's ask, on the grounds that a
 * cluster left alone should stay exactly as scattered as the spike left it and that merging is
 * something you *do* by driving one cell into another. That ask has been reversed by the player
 * who made it — see CLUSTER_MAGNET_SECONDS, which is a different mechanism rather than those two
 * constants restored: one pull toward the cluster's own centre at a fixed closing speed, rather
 * than a spring per pair that yanked a whole cluster together at once.
 *
 * The two do not fight, because they act at different moments and in that order: the magnet is
 * applied to steering in `stepMovement`, and this push is added *after* it in `stepCluster`, so a
 * pair on cooldown settles at the small overlap where the two balance (about eight world units)
 * and cannot stack. Once both are off cooldown the push stops and the magnet alone carries them
 * the rest of the way in, which is what makes the last stretch of a merge take a second or so
 * rather than happening on the tick a cooldown expires.
 */
export const SAME_PLAYER_SEPARATION = 0.25;

/**
 * How long a split cluster takes to pull itself back together, in seconds.
 *
 * **A CLUSTER IS A MAGNET, AND THIS REVERSES A DECISION THIS FILE ALREADY RECORDED.** The gather
 * half of the same-player spring was deleted at Andy's ask; Andy has asked for the opposite, with
 * the shape spelled out: "so after the initial burst when they spread out, they no longer can move
 * away from the larger circles and they actually slowly get pulled back into your larger circles
 * over time... it should take several seconds to remerge so other players can eat them."
 *
 * **IT IS A TIME RATHER THAN A SPEED, AND THAT IS THE SECOND VERSION OF THIS CONSTANT.** It
 * shipped as a flat 40 world units a second, on the reading that "some fixed magnet speed" was
 * what had been asked for. A fixed speed cannot hold a fixed time: what a split throws is
 * measured in the half's own radii (SPLIT_BURST_DISTANCE), so the gap is proportional to the
 * player's size and the time is too. Measured across four sizes at 40 u/s: **5.5 s at mass 200,
 * 9.1 s at 600, 16.1 s at 2 000 and 27.8 s at 6 000** — so the number Andy was watching depended
 * entirely on how big he happened to be, which is why "it's merging too fast" and "obviously
 * depends on how far away the circles are" were both true at once. Twelve seconds is his own
 * "should be 10... we may even want to go 15", and it is now the same twelve at every size.
 *
 * The point of the window is that a loose half is somebody else's meal: long enough for another
 * player to cross the distance and take one, which is the whole reason a split is a risk rather
 * than a free lunge.
 *
 * **IT HAS BEEN DOUBLED TWICE ON THE SAME REPORT, WHICH THE CAPTURE IS WHAT MAKES SAFE.** Twelve
 * became twenty-four ("double the amount of time across the board... they're merging too fast
 * still") and twenty-four is now forty-eight ("it's still too fast"). Because `magnetSpeed` is
 * captured once per settle from the cluster's own spread and divided by this, the scaling is
 * proportional: every measured figure in the matrix simply doubles with it, and the flat mass and
 * split-depth response the third attempt bought is untouched. Measured at 24 the matrix ran
 * 22.1 s to 27.0 s across four masses and four split depths, driving, standing still and
 * circling; at 48 it runs a shade over twice that. **The wall is the shortcut and does not move
 * with it** — convergence is never clamped, so pressing a cluster into an edge still merges it in
 * MERGE_COOLDOWN_TICKS and nothing more, which is the one thing a player can do about the wait.
 */
/**
 * How long the room keeps showing a player their own death, before anything else is allowed on
 * top of it.
 *
 * **IT IS SHARED BECAUSE BOTH SIDES HAVE TO HOLD THE SAME WINDOW.** The client uses it to keep the
 * death panel off and to keep the camera where the body was; the gateway uses it to keep sending
 * that socket an ordinary screenful of the room rather than the whole-map overview a dead player
 * settles into. Two numbers that had to agree would be one number to forget to change: if the
 * server switched first the pellet field would vanish mid-swallow, and if the client switched
 * first it would frame a map it had not been sent.
 *
 * Andy: "when a user gets swallowed, it should show the user eating them play out for like 2
 * seconds before showing the death and switching to the zoom out view, that way they can actually
 * see the player eating them." It was 0.9 s, which was enough to see *that* it happened and not
 * enough to see *who* — the killer is still arriving over those frames.
 *
 * Nothing is animated for it. The frames that draw the swallow are the ones already on the socket,
 * which the gateway keeps open past the `died` frame; this is only how long they are left alone.
 */
export const DEATH_SWALLOW_SECONDS = 2;
export const DEATH_SWALLOW_TICKS = Math.round(DEATH_SWALLOW_SECONDS * TICK_RATE_HZ);
export const DEATH_SWALLOW_MS = DEATH_SWALLOW_SECONDS * 1_000;

export const CLUSTER_MAGNET_SECONDS = 48;

/**
 * How a cluster's spread becomes a closing speed: multiply the mean distance of its cells from
 * their own centre by this, then divide by CLUSTER_MAGNET_SECONDS.
 *
 * **THE SPREAD IS MEASURED, NOT ASSUMED, AND THAT IS THE THIRD VERSION OF THIS MECHANIC.** It was
 * a flat speed (wrong: the time then scaled with the player's size), then a speed derived from the
 * cluster's *mass* on the assumption that a split settles a fixed number of radii apart — 3.7,
 * which is true of a single split and true of nothing else. Andy: "I was at 2500 and then when I
 * broke apart, it was divided by the 8 circles — so it was smaller. It should merge based on those
 * times based on the total mass, not the individual circle mass." He is right about the symptom
 * and the cause is one step further along: a cascade lands its pieces much closer together than a
 * single split does, so a speed sized for a 2 000-unit gap eats a 400-unit one in a fraction of
 * the time. Measured at mass 2 500: **11.6 s for a 2-cell split, 9.0 s at 4, 8.1 s at 8 and 7.8 s
 * at 16.**
 *
 * What predicts the time is the cluster's own geometry rather than its mass, and the scalar that
 * predicts it best is the **mean distance of the cells from their centroid**: the speed that would
 * have taken twelve seconds, divided by that distance, comes out 0.16, 0.18, 0.18 and 0.19 across
 * those four shapes. So the pull is proportional to how spread out the cluster actually is, and
 * one number covers a lunge, a cascade and a spike shatter alike.
 *
 * 2.1 is that ratio times CLUSTER_MAGNET_SECONDS, and it is tuned against the four shapes rather
 * than derived — which is why the test walks all four and holds each inside two seconds of the
 * constant. Nothing about it is a mass, so nothing about it goes wrong at a size nobody measured.
 *
 * **IT IS CAPTURED ONCE PER SETTLE RATHER THAN TRACKED**, which is the one thing that cannot be
 * left stateless. A speed recomputed from the *current* spread every tick is an exponential
 * approach: the cluster slows down as it closes, so a tight one still converges proportionally
 * faster and the times come out 15.4 / 11.2 / 8.3 / 8.1 across the four shapes instead of one
 * number. Raising the gain moves all four together and never flattens them. So
 * `PlayerState.magnetSpeed` holds what the pull was sized at when the cluster last settled — see
 * `RoomSim.stepClusterMagnet`.
 */
export const CLUSTER_MAGNET_SPREAD_GAIN = 2.1;

/**
 * Spike balls: static hazards that shatter the cell that runs into one.
 *
 * 24 across a 6000x6000 field puts them ~1 225 units apart on average, so a screenful holds one
 * or two of them — present enough to steer around, rare enough that crossing the map is
 * not an obstacle course. They are placed once at room creation and never move, are never eaten
 * and never respawn: a fixture is all this mechanic needs, and anything that grew or moved would
 * have to be replicated on a timer.
 */
export const SPIKE_COUNT = 24;

/** Fixed draw and collision radius; spikes do not grow, so this is not derived from mass. */
export const SPIKE_RADIUS = 60;

/**
 * THE FIELD IS A DENSITY, NOT A COUNT — the three derivations a per-room world size needs.
 *
 * PELLET_COUNT, SPIKE_COUNT and PELLET_HOTSPOT_COUNT above are all absolute numbers tuned at
 * WORLD_WIDTH x WORLD_HEIGHT, and every sentence justifying them is about how far apart the
 * things end up: "~181 units apart", "~1 225 units apart on average, so a screenful holds one or
 * two", "ten centres of radius 600 cover about a third of the world". Carrying those counts
 * unchanged onto a map four times as wide would divide every one of those distances' meaning by
 * sixteen — a bigger room would not be a bigger game, it would be an emptier one, with a
 * screenful of nothing between hotspots and a crossing that met no hazard at all.
 *
 * So each is re-read as its density and multiplied back up by `worldAreaRatio`. At the default
 * size the ratio is exactly 1 and each function returns exactly the constant it scales, which is
 * the property that keeps a default room byte-identical to the room it was before this existed;
 * `simulation.test.ts` pins that.
 *
 * Two things deliberately do NOT scale. `PELLET_HOTSPOT_RADIUS` and `SPIKE_RADIUS` are sizes a
 * *player* reads against their own blob and their own screen, not against the map — a hotspot
 * twice as wide on a map twice as wide would look identical from inside it, which is exactly the
 * landmark it is not supposed to be. Scaling the count instead keeps a hotspot the same object
 * and puts proportionally more of them out there. And `INTEREST_*` in `view.ts` is budgeted off
 * the density rather than the count (see FIELD_DENSITY), so holding density constant is also
 * what keeps a viewer's replication budget right on every size of map.
 */
function scaledByArea(base: number, worldWidth: number, worldHeight: number): number {
  return Math.max(1, Math.round(base * worldAreaRatio(worldWidth, worldHeight)));
}

/** Steady-state pellets for a room this size. PELLET_COUNT at the default, and its density above. */
export function pelletCountFor(worldWidth: number, worldHeight: number): number {
  return scaledByArea(PELLET_COUNT, worldWidth, worldHeight);
}

/** Spike balls for a room this size, at SPIKE_COUNT's ~1 225-unit mean spacing. */
export function spikeCountFor(worldWidth: number, worldHeight: number): number {
  return scaledByArea(SPIKE_COUNT, worldWidth, worldHeight);
}

/** Density hotspots for a room this size, so the same share of the map is hotspot. */
export function pelletHotspotCountFor(worldWidth: number, worldHeight: number): number {
  return scaledByArea(PELLET_HOTSPOT_COUNT, worldWidth, worldHeight);
}

/**
 * A hotspot's radius on a map this size — PELLET_HOTSPOT_RADIUS, except on a map too small to
 * hold one.
 *
 * Centres are placed a full radius inside every edge, so on a world narrower than four radii
 * that placement range would invert and there would be nowhere legal to put one. A quarter of
 * the shorter side is the widest disc that still leaves the centre somewhere to be; at every
 * size from the default upwards this is PELLET_HOTSPOT_RADIUS unchanged.
 */
export function pelletHotspotRadiusFor(worldWidth: number, worldHeight: number): number {
  return Math.min(PELLET_HOTSPOT_RADIUS, worldWidth / 4, worldHeight / 4);
}

/**
 * How far a spike has to be *inside* a cell before that cell shatters, as a fraction of the
 * spike's own diameter: 0 is the moment the two circles touch, 1 is the spike fully swallowed.
 *
 * A half means the shatter fires exactly when the spike's centre crosses the cell's edge — see
 * `spikeShatterDistance`, which is the one place that arithmetic lives and which the renderer
 * reads too. That matters because the spike is now drawn *on top* of the cell, with the cell's
 * outline indenting around it: what a player sees in the last moments before a shatter is the
 * spike pressing a dent into their blob, and the shatter has to land where the picture says it
 * will. The old threshold — the spike's centre within 40% of the cell's radius, borrowed from
 * the eat rule as it was then — was much deeper than that, so a big blob visibly swallowed a
 * spike whole and only then came apart.
 */
export const SPIKE_SHATTER_PENETRATION = 0.5;

/**
 * Minimum gap between two spikes when they are placed. Ten spike radii, so a cluster of them can
 * never form a wall a player has no line through. Placement retries this many times against the
 * constraint and then takes what it drew — a bounded, deterministic search, because an unbounded
 * one would make room creation depend on how lucky the seed was.
 */
export const SPIKE_MIN_SEPARATION = 600;
export const SPIKE_PLACEMENT_ATTEMPTS = 32;

/**
 * Below this mass a cell passes over a spike untouched.
 *
 * Twice STARTING_MASS: the smallest shatter is into two pieces, so a cell any lighter would be
 * broken into halves smaller than a fresh spawn — which reads as being deleted, not as being
 * broken up. It is also what makes spawning on top of a spike survivable: a player enters the
 * world at STARTING_MASS, so the hazard cannot touch them until they have eaten their way to
 * being worth breaking.
 *
 * It is a *per-cell* floor, and always was — but it only started to matter once the shatter
 * became per-cell too. A cluster of crumbs can now roll over a spike untouched, cell by cell.
 */
export const SPIKE_MIN_SHATTER_MASS = 2 * STARTING_MASS;

/**
 * The mass one fragment aims to be, which is what makes the shatter proportional: the cell that
 * hit the spike becomes `round(mass / SPIKE_FRAGMENT_MASS)` pieces, so a big cell breaks into
 * more (and still larger) pieces than a small one would. At 100 a cell must be worth five fresh
 * players before a spike does more than halve it, and reaches the per-cell cap at 800.
 *
 * Because only the cell that actually hit shatters, driving one of those pieces back into a spike
 * breaks that piece alone — and the arithmetic is run against *its* mass, which is how a player
 * ends up holding cells of visibly different sizes.
 */
export const SPIKE_FRAGMENT_MASS = 100;

/**
 * Ceiling on the pieces one cell may become — half the cluster cap, so one hit can never fill the
 * whole MAX_CELLS_PER_PLAYER budget and leave the player with no room to be broken again.
 */
export const SPIKE_MAX_FRAGMENTS_PER_CELL = MAX_CELLS_PER_PLAYER / 2;

/**
 * The bite the spike takes: 5% of the cell it shatters, gone from the world.
 *
 * Without it a spike would be a purely positional setback, and a large player could farm them —
 * pellet pickup is per cell, so a cluster deliberately spread across a wide field sweeps more
 * food than one circle of the same mass. A small, flat loss makes running into a spike strictly
 * bad without making it a death sentence.
 */
export const SPIKE_MASS_LOSS = 0.05;

/**
 * **A SPIKE IS CONSUMED BY THE CELL IT BREAKS, AND COMES BACK SOMEWHERE ELSE 15 SECONDS LATER.**
 *
 * It used to be a permanent fixture: a hazard placed once at construction and never moved, eaten
 * or destroyed. Andy: "the spikes should disappear when hit, not remain. And then they can
 * re-spawn in a different location on the map in 15 seconds." What that buys is a map that
 * changes over a round — a corridor a big player was avoiding opens up, and the corner they were
 * hiding in acquires a hazard — where a fixed field is learnt once and then simply avoided.
 *
 * Fifteen seconds is long enough for the gap to be worth taking and short enough that a room does
 * not slowly run out of hazards: at SPIKE_COUNT and the rate a busy room breaks them, the field
 * sits a spike or two under its target rather than emptying.
 */
export const SPIKE_RESPAWN_SECONDS = 15;
export const SPIKE_RESPAWN_TICKS = Math.round(SPIKE_RESPAWN_SECONDS * TICK_RATE_HZ);

/**
 * **HOW MANY EJECTED PELLETS A SPIKE SWALLOWS BEFORE IT BURSTS.**
 *
 * Andy: "when leaking part of the circle, users should be able to pump them into spike balls and
 * after like 5 or so, the spike ball will spawn a new one by bursting in the direction of the 5th
 * circle leak into it." So a spike is a thing you can *aim*: five ejections at one is a spike
 * launched at somebody, which is the genre's own virus-feeding move.
 *
 * Five rather than the genre's seven because an eject costs EJECT_MASS_COST and the room keeps
 * the difference, so the price of a launch is already real; and because five presses is one
 * held-down burst of the eject control rather than a chore.
 */
export const SPIKE_FEED_COUNT = 5;

/**
 * How far a burst spike travels, and over how long.
 *
 * **It is a distance a player can aim at somebody they can see** — a little over twice
 * MIN_VIEW_HALF_HEIGHT, so a burst launched at a neighbour on screen reaches them and one fired
 * at nothing lands in the open rather than crossing the map. The flight is deliberately slower
 * than a rocket's (ROCKET_SPEED is 900): a spike is a hazard being *placed*, and one that arrived
 * too fast to steer around would be a rocket with none of a rocket's cost.
 */
export const SPIKE_BURST_DISTANCE = 700;
export const SPIKE_BURST_TICKS = Math.round(1.4 * TICK_RATE_HZ);
export const SPIKE_BURST_SPEED = SPIKE_BURST_DISTANCE / (SPIKE_BURST_TICKS / TICK_RATE_HZ);

/**
 * **HOW MUCH A SPIKE SWELLS PER EJECTION IT SWALLOWS**, as a fraction of SPIKE_RADIUS.
 *
 * Andy: "the spike should actually show it's accepting the ejects by growing just a tad until it
 * bursts and shoots out the other one." Without it a fed spike looks exactly like an unfed one, so
 * the only way to know a shot landed is to count your own presses — and the one thing this
 * mechanic needs is for the fourth ejection to feel like the fourth.
 *
 * **THE SWELL IS REAL RATHER THAN DRAWN, and it is the same number on both sides.** A picture that
 * disagreed with the hazard would be worse than no picture: `spikeRadiusFor` is what the renderer
 * sizes the star from, what the outline dents against, and what a pellet has to reach to feed it.
 * What it deliberately does NOT move is when a spike breaks a cell — `spikeShatterDistance` is
 * measured to the spike's CENTRE (SPIKE_SHATTER_PENETRATION is 0.5, so the radius term is zero),
 * which is what makes swelling one safe to do at all: a fed spike is more visible and no more
 * dangerous, so nobody is broken by a hazard that grew into them.
 *
 * 5% a feed is four steps of 5% before it bursts — 60 units to 72 at its fullest, which is a
 * fifth. "Just a tad" per press, unmistakable by the last one, and back to 60 the instant it
 * fires.
 */
export const SPIKE_FED_GROWTH = 0.05;

/**
 * How big a spike is, given how many ejections it has swallowed.
 *
 * Shared for `spikeContactDistance`'s reason and the same one: the browser draws this and the
 * simulation collides against it, so there is one definition rather than two that agree today.
 */
export function spikeRadiusFor(fed: number): number {
  return SPIKE_RADIUS * (1 + SPIKE_FED_GROWTH * Math.max(0, fed));
}

/**
 * The largest a spike ever gets: one short of bursting.
 *
 * What it is *for* is grid queries. A cell looking for the spike it is touching has to search far
 * enough to find the biggest one there could be and then test each candidate at its own size —
 * searching at SPIKE_RADIUS would miss a swollen spike it was already inside.
 */
export const MAX_SPIKE_RADIUS = spikeRadiusFor(SPIKE_FEED_COUNT - 1);

/**
 * How long an outward impulse — a shatter's burst, and whatever a later powerup throws — keeps
 * pushing a cell before it is spent: 8 ticks, 0.4 s.
 *
 * Short on purpose. The impulse is a *burst*, not a second way to steer: while it runs it is
 * added on top of the cell's own steering velocity, so a window long enough to notice would be
 * a window in which the player is not driving their own cells. Long enough to read as an
 * explosion at 20 Hz (eight replicated frames), short enough that control comes straight back.
 *
 * It is a tick count rather than a duration for the same reason MERGE_COOLDOWN_TICKS is: a tick
 * count replays identically on every machine and a wall clock does not.
 */
export const BURST_IMPULSE_TICKS = 8;

/**
 * Where a shatter's pieces are *placed*, as a fraction of the ring they will settle on.
 *
 * Deep inside the body they were broken out of, not spread across it: pieces that appeared
 * already arranged at their resting distance would be the teleport this mechanic exists to stop
 * being. They emerge from within the parent and are thrown outward by the burst below, which is
 * what makes a shatter read as a body coming apart rather than as one being replaced by a
 * diagram of itself.
 *
 * Not zero, either. Coincident centres carry no bearing to separate along, so a ring — however
 * tight — is what keeps the pieces' arrangement a decision of the simulation rather than of the
 * arbitrary fallback direction `stepCluster` resolves a degenerate pair with.
 */
export const SHATTER_SPAWN_RING = 0.35;

/**
 * How far a piece is thrown, as a fraction of that same ring — so it overshoots where it belongs
 * by about a quarter and comes to rest a little outside it.
 *
 * Measured against where the pieces *end up* rather than against the parent, because that is the
 * quantity the burst has to stay in proportion to: 0.85 on top of SHATTER_SPAWN_RING peaks at
 * about 1.2 rings for two pieces and for sixteen alike, so a shatter reads as the same event
 * however many pieces it made, and a piece thrown short never leaves the body it came from.
 *
 * It used to be bounded above as well, by SAME_PLAYER_SETTLE_RANGE: a piece thrown further than
 * the gather spring reached was one the cluster could not draw back in. There is no gather spring
 * any more, so where the burst leaves a piece is simply where it stays until the player goes and
 * collects it — which is the point.
 */
export const SHATTER_BURST_DISTANCE = 0.85;

/**
 * Per-piece variation in that distance, ±30%, drawn from the room's seeded stream.
 *
 * A burst in which every piece travels exactly as far as every other is a ring expanding, not an
 * explosion. The jitter is what makes it look thrown — and because it comes from the room's own
 * PRNG in a fixed order, two hosts replaying the same seed throw it identically, which is what
 * keeps the burst inside `worldHash` rather than beside it.
 */
export const SHATTER_BURST_JITTER = 0.25;

/**
 * How far the smaller of two of your own cells has to be *inside* the larger before they fuse,
 * where 0 is first contact and 1 is completely swallowed.
 *
 * Merging used to happen on contact, which made re-forming a shattered cluster the easiest
 * interaction in the game — easier than eating somebody — and so took most of the cost out of
 * being broken apart. At 0.85 the smaller cell has to be most of the way in, which for two cells
 * of the same size puts their centres within 0.3 of a radius: deeper than the ~0.81 radii
 * EAT_COVERAGE_RATIO asks of an attacker swallowing a stranger of the same size, and deeper than
 * the rejoin spring alone reaches for as long as the player is steering. Putting a cluster back
 * together is therefore something you *do* — you drive the fast little pieces into the slow big
 * one, or you pin them against a wall and let them press in — rather than something that happens
 * to you on first contact.
 *
 * Measured against the smaller cell's diameter rather than the larger cell's radius on purpose,
 * and that is what makes the wall trick work at every size. Both cells are clamped to their own
 * radius at a world edge, so a crumb pressed into a wall beside a body can never get closer than
 * the difference of their radii — which for a big enough body is further than any fixed fraction
 * of *its* radius would allow. As a fraction of the small cell's own diameter the requirement
 * scales with the pair, so the last (1 - 0.85) of the way is always reachable.
 *
 * Nothing closes the last of that gap for the player. Once both cells are off cooldown there is
 * no same-player force at all — no push to fight and no pull to wait for — so the only thing that
 * puts a cluster back together is somebody steering one cell into another, which is exactly what
 * this ratio is here to make deliberate.
 */
export const MERGE_OVERLAP_RATIO = 0.85;

/**
 * Below this mass a cell refuses a player-initiated split.
 *
 * Twice STARTING_MASS, on exactly the reasoning SPIKE_MIN_SHATTER_MASS is set by: a split is a
 * halving, so anything lighter would hand the player two bodies smaller than a fresh spawn —
 * which reads as being deleted rather than as being divided. It is the *only* thing gating a
 * split, and that is deliberate: the card asks for three fast presses to give 2 -> 4 -> 8, so
 * there is no cooldown of its own here to slow the second press down.
 *
 * It is also what ends the cascade. A cluster of cells at exactly this mass splits into halves
 * at STARTING_MASS, and those cannot split again — so how far a player may divide is decided by
 * how much they have eaten, and MAX_CELLS_PER_PLAYER is the ceiling only for a body big enough
 * to reach it.
 */
export const SPLIT_MIN_MASS = 2 * STARTING_MASS;

/**
 * How far the new half is thrown, as a multiple of *its own* radius: four radii, two of its
 * diameters, over BURST_IMPULSE_TICKS.
 *
 * Measured against the half rather than against the parent so that a split reads as the same
 * event at every size — a crumb lunges a crumb's distance and a leader lunges a leader's — and
 * because it is the half that has to arrive somewhere, not the body it came out of. Four is what
 * makes the lunge worth taking: over 0.4 s it is several times the speed the mass curve allows a
 * cell of that size, which is the whole mechanic the card asks for. A smaller circle can outrun
 * a bigger one, and a split is how the bigger one stops being outrun — at the price of half its
 * mass sitting out in front of it, unmergeable for MERGE_COOLDOWN_TICKS.
 *
 * Only the new half is thrown. The parent stays where it was, holding the other half, which is
 * what makes a split a lunge in a direction rather than a body coming apart in place — the
 * difference between this and the spike shatter's SHATTER_BURST_DISTANCE, which throws every
 * piece it makes.
 */
export const SPLIT_BURST_DISTANCE = 4;

// ---------------------------------------------------------------------------
// EJECT — shooting an oversized pellet out as bait.
//
// The second thing a player may spend mass on, and the opposite trade to the split: a split buys
// reach with the risk of being in pieces, an eject buys somebody else's *attention* with mass
// that leaves your body entirely. What is thrown is an ordinary pellet from the moment it lands
// (EJECTED_PELLET_MASS, up beside the rest of the pellet band), so anybody may eat it —
// including, after a delay, the player who threw it.
// ---------------------------------------------------------------------------

/**
 * What one cell pays to eject, whatever size it is.
 *
 * Fixed rather than proportional, which is what makes the action mean different things at
 * different sizes: to a cell at EJECT_MIN_MASS it is nearly half the body and a real decision, to
 * a leader it is a rounding error they can spend all day. That asymmetry is the mechanic — a big
 * player baiting a small one is spending almost nothing, and the small one is being offered
 * almost half of what the bait cost.
 *
 * It is more than the pellet is worth (EJECTED_PELLET_MASS), so a player who ejects and eats
 * their own dot back is down a quarter of what they spent. Bait you did not sell is bait that
 * cost you something, and the room keeps the difference — which is also what stops eject-and-eat
 * from being a way to launder mass past passive decay.
 */
export const EJECT_MASS_COST = 16;

/**
 * Below this mass a cell refuses to eject — STARTING_MASS plus the cost.
 *
 * Stated as the sum rather than as a number, because that sum is the property that matters: a
 * cell can never eject itself below the size it spawned at, so no amount of pressing the button
 * is a way to die, and the floor moves with STARTING_MASS if that is ever retuned. It is the
 * *only* gate on the action, exactly as SPLIT_MIN_MASS is the only gate on a split — there is no
 * cooldown of its own, and the room cap (MAX_EJECTED_PELLETS_PER_ROOM) is what bounds a player
 * who holds the button down.
 */
export const EJECT_MIN_MASS = STARTING_MASS + EJECT_MASS_COST;

/**
 * How far the pellet is thrown, in world units, over BURST_IMPULSE_TICKS.
 *
 * An absolute distance rather than the multiple-of-a-radius SPLIT_BURST_DISTANCE uses, because
 * what is thrown here is always the same size whoever threw it: a bait dot has to land somewhere
 * a *third party* will notice it, and that distance is a property of the map and of how far a
 * player can see, not of how big the thrower is. It is launched from the ejecting cell's rim, so
 * a leader's bait still clears their own body first.
 *
 * **IT WAS 200 AND IS 400, AT ANDY'S ASK** ("the eject pellets should shoot about twice as far"),
 * and what the first figure got wrong is visible in its own justification: it was chosen so that
 * "the thrower is still the nearest threat to whoever comes for it", which is bait you have not
 * really thrown — 200 units is about a third of a fresh player's own view, so the dot lands
 * inside the mouth it came out of. At 400 it clears the thrower's screen edge for a small player
 * and is a real distance for a big one, which is what makes it a *lure* rather than a decoration:
 * somebody has to come to it, and coming to it is the risk the mechanic is about.
 *
 * It is also what makes feeding a spike a thing you can do from outside its reach — see
 * SPIKE_FEED_COUNT — rather than a thing you have to drive on top of the hazard to attempt.
 *
 * An absolute distance rather than the multiple-of-a-radius SPLIT_BURST_DISTANCE uses, for the
 * reason above it, and the flight is still BURST_IMPULSE_TICKS: what doubled is the speed, so a
 * bait still lands in 0.4 s and the field is never carrying a dot that is travelling for long.
 */
export const EJECT_THROW_DISTANCE = 400;

/**
 * How long before the player who ejected a pellet may eat it back — twice the flight, 0.8 s.
 *
 * Without it the dot would be swallowed on the tick it was made: it leaves from the rim of the
 * cell that threw it, and that cell is moving in the same direction. Twice BURST_IMPULSE_TICKS
 * rather than exactly it, so the pellet has come to rest and been drawn where it landed before it
 * is takeable — a bait that vanished mid-flight would read as the game eating it.
 *
 * It applies to the thrower alone. Everybody else may take it from the first tick, which is the
 * whole point of throwing it.
 */
export const EJECT_SELF_PICKUP_TICKS = 2 * BURST_IMPULSE_TICKS;

/**
 * How many ejected pellets one room will hold at once. Past this an eject is refused, silently,
 * exactly as a split under SPLIT_MIN_MASS is.
 *
 * THE CAP IS WHY EJECTED PELLETS CANNOT ACCUMULATE. They are ordinary pellets in every other
 * respect — they sit in the same map, are found by the same grid query and are eaten by the same
 * phase — so they also count toward the room's pellet target, and a room carrying bait respawns
 * nothing of its own until the field falls back under PELLET_COUNT. Together the two bound the
 * whole field at PELLET_COUNT + MAX_EJECTED_PELLETS_PER_ROOM, however long anybody holds the
 * button down; this is the half that bounds how much bait can be *outstanding* while nobody eats
 * it.
 *
 * 200 is two per seat in a DEFAULT_ROOM_PLAYERS room and 6% of PELLET_COUNT — anchored to the
 * room size the field was tuned at rather than to the ROOM_MAX_PLAYERS ceiling, because what
 * this bounds is the field, and the field is the same 6 000 x 6 000 either way. Enough that a room where
 * everybody is baiting at once never notices the ceiling, small enough that the worst case is a
 * food field 6% denser than the tuned one and always overwhelmingly the room's own.
 */
export const MAX_EJECTED_PELLETS_PER_ROOM = 200;

// ---------------------------------------------------------------------------
// POWER-UPS — the glowing gold orbs, and the substrate under them.
//
// An orb is rare on purpose. It is the one thing on the field that is not food, not a hazard and
// not another player, so what makes it worth crossing the map for is that there is almost never
// one to cross the map for. The numbers below are set to that: a room holds a couple of them at
// a time in a 6 000 x 6 000 world, which is roughly a one-in-four chance that any given player
// has one on screen.
//
// Which power-up an orb is comes from the registry in `../powerups`, never from here.
// ---------------------------------------------------------------------------

/**
 * How big an orb is drawn and how far its pickup reaches, in world units.
 *
 * Bigger than a fully grown pellet (7) and than the oversized bait dot (12) and a shade bigger
 * than a fresh player (17.9), which is the whole of the visual grammar: on a field of dots, the
 * thing that is neither a dot nor a body is the thing worth going to look at. It is also what
 * lets an orb read on the minimap, where a pellet cannot.
 *
 * A fixture's size, not state: like SPIKE_RADIUS it is a shared constant rather than a wire
 * field, so a `PowerUpSnapshot` is an id, a kind and a place and nothing else.
 */
export const POWER_UP_RADIUS = 22;

/**
 * Mean seconds between one orb appearing and the next.
 *
 * Thirty against POWER_UP_LIFETIME_SECONDS of sixty puts the steady state at about two orbs
 * alive in the room — the equilibrium of a Poisson arrival with a fixed lifetime is
 * lifetime / interval — well under MAX_POWER_UPS_PER_ROOM, so the cap is a backstop against a
 * quiet room saving them up rather than the thing that sets the density.
 *
 * Two orbs in 36 000 000 square units, against a camera that frames a few million of them, is
 * "you see one every few minutes and you remember it". Faster and an orb becomes another pellet;
 * slower and most players would finish a run having never met one, which is a mechanic nobody
 * would learn.
 */
export const POWER_UP_MEAN_SPAWN_SECONDS = 30;

/**
 * The per-opportunity spawn probability, derived rather than written down.
 *
 * Spawning is evaluated on the `powerup` tier's own divisor (UPDATE_INTERVAL_TICKS.powerup, 5 Hz)
 * rather than every tick, because that is the reserved seam this mechanic fills and because a
 * once-every-thirty-seconds event has no business asking a question twenty times a second. The
 * probability is therefore one over the number of opportunities in POWER_UP_MEAN_SPAWN_SECONDS,
 * which keeps the *rate* fixed if the tier is ever re-tuned.
 */
export const POWER_UP_SPAWN_OPPORTUNITIES_PER_SECOND = TICK_RATE_HZ / UPDATE_INTERVAL_TICKS.powerup;
export const POWER_UP_SPAWN_CHANCE =
  1 / (POWER_UP_MEAN_SPAWN_SECONDS * POWER_UP_SPAWN_OPPORTUNITIES_PER_SECOND);

/**
 * How long an orb nobody took lies on the field, in seconds and in ticks.
 *
 * It despawns rather than waiting forever, and that is what stops the map slowly filling up with
 * orbs in the corners nobody visits — the cap alone would do it eventually, but it would do it by
 * refusing to spawn new ones where the players actually are. A minute is long enough that an orb
 * seen across the map is worth turning for and short enough that a room's orbs are always roughly
 * where somebody has been recently.
 */
export const POWER_UP_LIFETIME_SECONDS = 60;
export const POWER_UP_LIFETIME_TICKS = POWER_UP_LIFETIME_SECONDS * TICK_RATE_HZ;

/**
 * The most orbs one room will hold at once.
 *
 * A backstop rather than a target — see POWER_UP_MEAN_SPAWN_SECONDS, which is what actually sets
 * the density at about two. Four is double that, so a room that has been quiet for a while can be
 * carrying a small surplus without the cap ever being the thing a player notices, and it bounds
 * what an orb costs replication at four entities on a 5 Hz tier however the rate is retuned.
 */
export const MAX_POWER_UPS_PER_ROOM = 4;

/**
 * How many DISTINCT power-ups a player may be holding at once. **Three.**
 *
 * This used to be one, on the reasoning that holding one made picking one up a commitment and
 * that anything deeper "would need a slot picker on the wire, in the HUD and on a phone's thumb".
 * All of that was true and the player asked for it anyway: "A user should also be able to collect
 * multiple power ups and switch between them to use different ones." So the slot picker exists —
 * `clientInputSchema.selectSlot`, which names an INDEX and never a kind.
 *
 * **Three, and the bound is a thumb rather than an argument about fun.** Three is what an action
 * row on a phone can show beside split and eject without the row becoming the game, and it is the
 * smallest number at which "switch between them" is a real choice — with two, switching is a
 * toggle. It is also what keeps a full inventory cheap on the wire: three short ids and two small
 * varints each, on the viewer's own frame only.
 *
 * **Distinct**, and that is the other half of the rule: an orb of a kind already held **tops that
 * slot up** rather than being refused or taking a second slot, so a player cannot bank six
 * rockets, and driving over your own kind is never a wasted orb. `RoomSim.stepPowerUpPickups` is
 * where both halves live.
 */
export const POWER_UP_INVENTORY_SIZE = 3;

/**
 * How far from a world edge an orb is placed.
 *
 * The same margin a player spawns inside (SPAWN_EDGE_MARGIN), rather than the orb's own radius:
 * an orb pressed into a corner is one only a player who is already there can reach, because a
 * cluster clamped against two walls cannot centre itself on it.
 */
export const POWER_UP_EDGE_MARGIN = SPAWN_EDGE_MARGIN;

/**
 * The most projectiles one room will hold at once — rockets in flight, mines lying about and
 * grapple chains together.
 *
 * A backstop rather than a target, exactly as MAX_POWER_UPS_PER_ROOM is, and the number that
 * bounds what the `projectile` tier can ever cost: projectiles are replicated room-wide at 20 Hz,
 * so this is the ceiling on the entities that ride it however the abilities are retuned.
 *
 * What actually sets the population is the orb field upstream of it — one orb every
 * POWER_UP_MEAN_SPAWN_SECONDS, at most MAX_POWER_UPS_PER_ROOM on the field, one slot per player —
 * which puts the steady state at a handful even in a full room. Thirty-two is far enough above
 * that to be unreachable by ordinary play and low enough that a room saturated with mines is
 * still a room and not a minefield. Past it a firing is refused *and the power-up is kept*: the
 * cap is the room's problem, not the player's, and a slot emptied for nothing would be.
 */
export const MAX_PROJECTILES_PER_ROOM = 32;

/**
 * How far outside the firing cell's rim a launched projectile starts, in world units.
 *
 * Clear of the body that fired it, for the reason an ejected pellet leaves from the rim plus its
 * own radius: a rocket born inside its owner would spend its first ticks inside a cell it is not
 * allowed to hit, and a grapple head would already be past the target it was aimed at.
 */
export const PROJECTILE_MUZZLE_CLEARANCE = 6;

/**
 * The widest a cell can ever be, and therefore how far from a projectile a cell's *centre* may
 * be while its rim is still touching — the radius the broadphase has to ask about.
 *
 * Derived rather than written down so it cannot drift from MAX_PLAYER_MASS. It is deliberately
 * the ceiling and not a typical size: a query that missed the one 693-unit leader in the room
 * would be a rocket that passed through them.
 */
export const PROJECTILE_QUERY_REACH = MASS_RADIUS_COEFFICIENT * Math.sqrt(MAX_PLAYER_MASS);

// ---------------------------------------------------------------------------
// PRIZE SHARDS — the pot a bust scatters on the floor.
//
// A shard is mass that has left a player's body and is lying in the open for anybody to take,
// which is the ejected bait dot's shape (EJECT_MASS_COST) at a completely different scale: a bait
// is twelve mass a leader spends without noticing, and a shard is a share of a pot two players
// gambled and lost. Three things follow from that and every number below is one of them.
//
// **THE VALUE IS HIDDEN AND IS NOT ON THE WIRE.** A shard replicates as an id and a place, the
// same shape a spike and an orb replicate as; what it is worth is applied server-side on pickup.
// That is not only a saving — PELLET_WIRE_MASS_MAX is 12, so a shard worth hundreds could not
// have ridden the pellet channel at all, and not sending the value removes the problem instead of
// quantising it.
//
// **ANYBODY MAY EAT ONE, WHATEVER SIZE THEY ARE.** A shard is food, not a player, so the
// EAT_MASS_RATIO rule that decides who may swallow whom does not apply — see
// `RoomSim.stepPrizeShardPickups`, which is where that exception is written down.
//
// **NOTHING PRODUCES ONE YET.** `RoomSim.spawnPrizeShards` is the entry point; the card that
// busts a pot is what will call it.
// ---------------------------------------------------------------------------

/**
 * How many shards one bust scatters. **Five.**
 *
 * Several rather than one, because a single dot holding the whole pot rewards whoever happens to
 * be standing nearest and is over in one mouthful; five is a scramble, and a scramble is a thing
 * the rest of the room can join.
 *
 * Five rather than four for one reason worth stating: it is **odd**, so the two players who
 * forfeited the pot cannot take half each and walk away square — however the race goes, one of
 * the five decides it. Against PRIZE_SHARD_SCATTER_RADIUS it is also about as many as a ring that
 * size holds without two of them being one mouthful: a mid-size cluster covers one at a time.
 */
export const PRIZE_SHARD_COUNT = 5;

/**
 * How big a shard is drawn and how far its pickup reaches, in world units.
 *
 * Bigger than the oversized bait dot (EJECTED_PELLET_RADIUS, 12) and smaller than an orb
 * (POWER_UP_RADIUS, 22), which is the whole of the visual grammar: on a field of dots the thing
 * that is neither a dot nor an orb nor a body is the thing that just fell out of somebody. It is
 * also what lets a shard read on the minimap, where a pellet cannot.
 *
 * A fixture's size, not state: like SPIKE_RADIUS and POWER_UP_RADIUS it is a shared constant
 * rather than a wire field, so a shard's snapshot stays an id and a place.
 */
export const PRIZE_SHARD_RADIUS = 16;

/**
 * How far from the bust a shard may land, in world units.
 *
 * **260 is under MIN_VIEW_HALF_HEIGHT (320), and that is the number it was chosen against**: the
 * narrowest band on the ladder frames 320 units above and below the camera, so every one of the
 * five is on screen for somebody standing where the pot burst — including the smallest player in
 * the room, who is the one framed narrowest. A scatter wider than the view would be shards a
 * player has to go looking for rather than race for.
 *
 * It is wider than EJECT_THROW_DISTANCE (200) on purpose. A bait is thrown to be *found*; this is
 * thrown to be *spread*, so that no single cluster is sitting on the whole pot when it lands.
 */
export const PRIZE_SHARD_SCATTER_RADIUS = 260;

/**
 * How long the players who forfeited the pot must wait before they may take their own shards
 * back — three seconds, and it is deliberately much longer than EJECT_SELF_PICKUP_TICKS (0.8 s).
 *
 * The eject's delay exists because a bait leaves the rim of a mouth that is moving the same way,
 * so it is only ever about the flight. This one is about a *race*: the two who busted are
 * standing on top of the shards at the moment they appear, and a delay that only cleared the
 * scatter would hand the pot straight back to them.
 *
 * Three seconds is 1 080 units of travel at BASE_SPEED, which is four PRIZE_SHARD_SCATTER_RADIUS
 * and comfortably past the edge of a mid-band viewport — so a player who *saw* the bust happen
 * from across their own screen arrives with time in hand, which is the whole point of scattering
 * the pot in public. It is not so long that a forfeiter has to give up: they are standing there,
 * and what the three seconds buys everybody else is a start rather than the pot.
 *
 * It applies to the forfeiters alone. Everybody else may take a shard on the tick it lands.
 */
export const PRIZE_SHARD_SELF_PICKUP_SECONDS = 3;
export const PRIZE_SHARD_SELF_PICKUP_TICKS = PRIZE_SHARD_SELF_PICKUP_SECONDS * TICK_RATE_HZ;

/**
 * How long a shard nobody took lies on the field — thirty seconds, half an orb's minute.
 *
 * It despawns for POWER_UP_LIFETIME_SECONDS' reason: without it the map slowly fills up with
 * shards in the corners nobody visits, and a shard is worth far more than an orb, so a corner
 * nobody visits would eventually be worth crossing the map for on a bust that happened ten
 * minutes ago. Half a minute rather than a whole one because the shards ARE the event: the whole
 * of a bust is the scramble it starts, and one still lying there after thirty seconds is one
 * nobody is racing for.
 *
 * **An expired shard is mass the room keeps**, which is the one place this mechanic is not
 * zero-sum and it is the same trade EJECT_MASS_COST already makes: mass nobody collected is mass
 * that cost somebody something. Everything else about it is exactly zero-sum — see
 * `RoomSim.spawnPrizeShards`.
 */
export const PRIZE_SHARD_LIFETIME_SECONDS = 30;
export const PRIZE_SHARD_LIFETIME_TICKS = PRIZE_SHARD_LIFETIME_SECONDS * TICK_RATE_HZ;
